import { useMemo, useState } from 'react'

import { useAppSelector } from 'redux/hooks'
import { CardType, EDH_FORMATS } from 'types/deck'
import { Option } from 'components/formElements/Dropdown'

import PhatButton from 'components/formElements/PhatButton'
import PhatDropdown from 'components/formElements/PhatDropdown'
import QuantityInput from 'components/card/cardForm/quantityInput'
import ManaSymbol from 'components/misc/customIcons/ManaSymbol'

import { getSubtypesForDeckData, getTypesForDeckData } from 'utils/mtgColor.utils'

import parentStyles from '../probability.module.scss'
import styles from './selectingAndClause.module.scss'

export type ProbabilityLogic = 'atLeast' | 'exactly'
export type AndClause = { label: string; successes: number; successesInSample: number; logic: ProbabilityLogic }

export const defaultAndClasue: AndClause = {
  label: '',
  successes: 0,
  successesInSample: 1,
  logic: 'atLeast',
}

type Props = {
  value: AndClause
  onCancel: (e: React.MouseEvent) => void
  onUpdate: (clause: AndClause) => void
}

const SelectingAndClause = ({ value, onCancel, onUpdate }: Props) => {
  const cardMap = useAppSelector(state => state.deck.cardMap)
  const categories = useAppSelector(state => state.deck.categories)
  const format = useAppSelector(state => state.deck.format)

  const options: Option[] = useMemo(() => {
    const maindeck: CardType[] = Object.values(cardMap).filter(card => {
      const primaryCategoryName = card.categories[0]
      const primaryCategory = categories[primaryCategoryName]

      return !primaryCategory.isPremier && primaryCategory.includedInDeck && primaryCategory.name !== 'Sideboard'
    })

    const cardNameQuantities: Record<string, number> = {}
    const cardTypeQuantities: Record<string, number> = {}
    const cardManaValueQuantities: Record<number, number> = {}
    const cardCategoryQuantities: Record<string, number> = {}
    const cardSubTypeQuantities: Record<string, number> = {}

    for (const card of maindeck) {
      if (!cardNameQuantities[card.name]) cardNameQuantities[card.name] = 0
      cardNameQuantities[card.name] += card.qty

      if (!cardManaValueQuantities[card.cmc]) cardManaValueQuantities[card.cmc] = 0
      cardManaValueQuantities[card.cmc] += card.qty

      const types = getTypesForDeckData(card)
      const subTypes = getSubtypesForDeckData(card)

      for (const type of types) {
        if (!cardTypeQuantities[type]) cardTypeQuantities[type] = 0
        cardTypeQuantities[type] += card.qty
      }

      for (const category of card.categories) {
        if (!cardCategoryQuantities[category]) cardCategoryQuantities[category] = 0
        cardCategoryQuantities[category] += card.qty
      }

      for (const subType of subTypes) {
        if (!cardSubTypeQuantities[subType]) cardSubTypeQuantities[subType] = 0
        cardSubTypeQuantities[subType] += card.qty
      }
    }

    const convertQuantitiesToOptions = (header: string, quantities: Record<string, number>) => [
      { type: 'header', label: header, value: undefined },
      ...Object.keys(quantities)
        .sort((a, b) => (a > b ? 1 : -1))
        .map(label => ({
          label:
            header === 'Mana values' ? (
              <span className={styles.manaValue}>
                <ManaSymbol symbol={label} /> MV
              </span>
            ) : (
              label
            ),
          id: label,
          value: label,
          onClick: () => onUpdate({ label, successes: quantities[label], successesInSample: 1, logic: 'atLeast' }),
          className: styles.optionPadding,
        })),
      { type: 'spacer', value: undefined, label: undefined },
    ]

    if (EDH_FORMATS.includes(format)) {
      return [
        ...convertQuantitiesToOptions('Categories', cardCategoryQuantities),
        ...convertQuantitiesToOptions('Mana values', cardManaValueQuantities),
        ...convertQuantitiesToOptions('Types', cardTypeQuantities),
        ...convertQuantitiesToOptions('Subtypes', cardSubTypeQuantities),
        ...convertQuantitiesToOptions('Card names', cardNameQuantities),
      ]
    }

    return [
      ...convertQuantitiesToOptions('Card names', cardNameQuantities),
      ...convertQuantitiesToOptions('Categories', cardCategoryQuantities),
      ...convertQuantitiesToOptions('Mana values', cardManaValueQuantities),
      ...convertQuantitiesToOptions('Types', cardTypeQuantities),
      ...convertQuantitiesToOptions('Subtypes', cardSubTypeQuantities),
    ]
  }, [cardMap, categories, format])

  return (
    <div className={parentStyles.primaryControls}>
      <label>Probability of drawing</label>
      <PhatDropdown
        value={value.logic}
        options={[
          { label: 'At least', onClick: () => onUpdate({ ...value, logic: 'atLeast' }), id: 'atLeast' },
          { label: 'Exactly', onClick: () => onUpdate({ ...value, logic: 'exactly' }), id: 'exactly' },
        ]}
        className={parentStyles.logicControls}
      />
      <QuantityInput
        dontAllowZero
        noLabel
        quantity={value.successesInSample}
        onQuantityUpdate={successesInSample => onUpdate({ ...value, successesInSample })}
        className={parentStyles.quantityInput}
      />
      <PhatDropdown
        placeholder="Choose data slice"
        value={value.label}
        options={options}
        className={styles.dataSliceSelector}
      />

      <label>card(s)</label>

      <PhatButton onClick={onCancel} color="red" className={styles.removeButton} icon="close" />
    </div>
  )
}

export default SelectingAndClause
